import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { DropdownItem } from '@widgets/dropdown/dropdown-type';
import {
  AlertFilter,
  AlertType,
  BusinessPartner,
  MutedAlertFilter,
  MuteStationRequest,
  PartnerLocation,
  UpdateAlertConfiguration,
} from '../data/alerting.models';
import { AlertingActions } from './alerting.actions';
import {
  alerts,
  configurations,
  excelDownloadInProgress,
  groups,
  loadingAlertDetailsFor,
  loadingAlerts,
  loadingConfigurations,
  loadingGroups,
  loadingMutedStations,
  loadingRecipientGroups,
  locations,
  mutedStations,
  mutedStationsPaging,
  noStationsFound,
  paging,
  partnerList,
  recipientGroups,
  selectedAlert,
  selectedGroup,
  selectedLocation,
  selectedPartner,
  selectedRecipientGroup,
} from './alerting.selector';

@Injectable({
  providedIn: 'root',
})
export class AlertingFacade {
  private readonly ngrxStore = inject(Store);

  readonly businessPartners$ = this.ngrxStore.pipe(select(partnerList));
  readonly selectedPartner$ = this.ngrxStore.pipe(select(selectedPartner));
  readonly locations$ = this.ngrxStore.pipe(select(locations));
  readonly selectedLocation$ = this.ngrxStore.pipe(select(selectedLocation));
  readonly alerts$ = this.ngrxStore.pipe(select(alerts));
  readonly loadingAlertDetailsFor$ = this.ngrxStore.pipe(select(loadingAlertDetailsFor));
  readonly selectedAlert$ = this.ngrxStore.pipe(select(selectedAlert));
  readonly loadingAlerts$ = this.ngrxStore.pipe(select(loadingAlerts));
  readonly noStationsFound$ = this.ngrxStore.pipe(select(noStationsFound));
  readonly paging$ = this.ngrxStore.pipe(select(paging));
  readonly groups$ = this.ngrxStore.pipe(select(groups));
  readonly loadingGroups$ = this.ngrxStore.pipe(select(loadingGroups));
  readonly selectedGroup$ = this.ngrxStore.pipe(select(selectedGroup));
  readonly loadingConfigurations$ = this.ngrxStore.pipe(select(loadingConfigurations));
  readonly configurations$ = this.ngrxStore.pipe(select(configurations));
  readonly recipientGroups$ = this.ngrxStore.pipe(select(recipientGroups));
  readonly loadingRecipientGroups$ = this.ngrxStore.pipe(select(loadingRecipientGroups));
  readonly selectedRecipientGroup$ = this.ngrxStore.pipe(select(selectedRecipientGroup));
  readonly excelDownloadInProgress$ = this.ngrxStore.pipe(select(excelDownloadInProgress));
  readonly mutedStations$ = this.ngrxStore.pipe(select(mutedStations));
  readonly loadingMutedStations$ = this.ngrxStore.pipe(select(loadingMutedStations));
  readonly mutedStationsPaging$ = this.ngrxStore.pipe(select(mutedStationsPaging));

  getPartnerList(): void {
    this.ngrxStore.dispatch(AlertingActions.getPartners());
  }

  selectPartner(partner: DropdownItem<BusinessPartner>): void {
    this.ngrxStore.dispatch(
      AlertingActions.selectPartner({
        partner: partner,
      })
    );
  }

  selectLocation(location: DropdownItem<PartnerLocation>): void {
    this.ngrxStore.dispatch(
      AlertingActions.selectLocation({
        location: location,
      })
    );
  }

  getAlerts(filter: AlertFilter) {
    this.ngrxStore.dispatch(AlertingActions.getAlerts({ filter }));
  }

  selectAlert(alertId: number, partnerId: string, alertType: AlertType) {
    if (alertType === AlertType.SALESFORCE_TICKET) {
      this.ngrxStore.dispatch(AlertingActions.selectEvDriverAlert({ alertId, partnerId }));
    } else if (alertType === AlertType.CHARGING_SESSION) {
      this.ngrxStore.dispatch(AlertingActions.selectSessionAlert({ alertId, partnerId }));
    } else {
      this.ngrxStore.dispatch(AlertingActions.selectAlert({ alertId, partnerId }));
    }
  }

  getAlertsNextPage(filter: AlertFilter) {
    this.ngrxStore.dispatch(AlertingActions.getAlertsNextPage({ filter }));
  }

  maskAsResolved(alertId: number, partnerId: string): void {
    this.ngrxStore.dispatch(AlertingActions.markAsResolved({ alertId, partnerId }));
  }

  getGroups(selectedGroupId?: number) {
    this.ngrxStore.dispatch(AlertingActions.getGroups({ selectedGroupId }));
  }

  selectGroup(groupId: number) {
    this.ngrxStore.dispatch(AlertingActions.selectGroup({ groupId: groupId }));
  }

  unselectGroup() {
    this.ngrxStore.dispatch(AlertingActions.unselectGroup());
  }

  getConfigurations() {
    this.ngrxStore.dispatch(AlertingActions.getConfigurations());
    this.ngrxStore.dispatch(AlertingActions.getGroups({}));
    this.ngrxStore.dispatch(AlertingActions.getRecipientGroups({}));
  }

  getRecipientGroups(selectedRecipientGroupId?: number) {
    this.ngrxStore.dispatch(AlertingActions.getRecipientGroups({ selectedRecipientGroupId }));
  }

  selectRecipientGroups(recipientGroupId: number) {
    this.ngrxStore.dispatch(
      AlertingActions.selectRecipientGroup({ recipientGroupId: recipientGroupId })
    );
  }

  unselectRecipientGroup() {
    this.ngrxStore.dispatch(AlertingActions.unselectRecipientGroup());
  }

  saveConfiguration(configuration: UpdateAlertConfiguration, alertType: AlertType) {
    this.ngrxStore.dispatch(
      AlertingActions.saveConfigurations({ configuration: configuration, alertType: alertType })
    );
  }

  toggleAlertType(alertType: AlertType, value: boolean) {
    this.ngrxStore.dispatch(
      AlertingActions.toggleAlertType({ alertType: alertType, value: value })
    );
  }

  getMutedStations(filter: MutedAlertFilter) {
    this.ngrxStore.dispatch(AlertingActions.getMutedStations({ filter }));
  }

  getMutedStationsNextPage(filter: MutedAlertFilter) {
    this.ngrxStore.dispatch(AlertingActions.getMutedStationsNextPage({ filter }));
  }

  unmuteStation(stationId: string, partnerId: string): void {
    this.ngrxStore.dispatch(AlertingActions.unmuteStation({ stationId, partnerId }));
  }

  muteStation(stationId: string, partnerId: string, muteRequest: MuteStationRequest): void {
    this.ngrxStore.dispatch(AlertingActions.muteStation({ stationId, partnerId, muteRequest }));
  }

  exportAlerts(filter: AlertFilter) {
    this.ngrxStore.dispatch(AlertingActions.exportAlerts({ filter }));
  }
}
